// packages/web/src/process/ProcessTree.tsx
//
// Structural sidebar tree for the active process. Reads a WalkResult
// (from useProcessWalker) and a WalkerState; emits selection callbacks
// for branch picks, subprocess expansion, and scrubber jumps.
//
// Presentation (2026-07 rework): construct rows carry the BPMN glyph
// vocabulary (× if · ◯ select · + parallel · ↻/≡ each · ⚡ try · ⊞ do), rails
// and owner chips tint through the unified "colour by" layer (`ownerColor` —
// same source as the board/BPMN views), unselected alternative branch labels
// are clickable picks on exclusive frames, and the active step row glows like
// the diagram node. All data-* hooks, texts and callbacks predate the rework.

import React from "react";
import type { WalkResult, WalkNode, WalkBranch, NodeId, QualifiedName } from "@archlang/engine";
import { isAwaitNodeId } from "@archlang/engine";
import { handleDescriptionClick, renderDescriptionHtml } from "../description/render.js";
import { isFrameExclusive } from "./frameAffordance.js";
import { displayLabelFor } from "./displayNumbers.js";
import { Button, IconButton, Tooltip, cx, FOCUS_RING, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { StepEditMenu } from "./StepEditMenu.js";
import { InlineEditInput } from "./InlineEditInput.js";
import type { ProcessStepEditApi, RemovableArm } from "./processStepEdit.js";
import { blockMorphCaps, type BlockMorphCaps } from "./processBlockMorph.js";
import {
  mergeCapsFor,
  selectCountCapsFor,
  isAwaitBoundaryParallel,
  type MergeCaps,
  type SelectCountCaps,
} from "./processMergeCaps.js";
import {
  useProcessSelection,
  orderedStepIds,
  clickModifiers,
  type ProcessSelection,
} from "./processSelection.js";
import { useTreeStepDrag, type TreeDragApi } from "./useTreeStepDrag.js";
import { ProcessSelectionToolbar } from "./ProcessSelectionToolbar.js";
import { selectedStepsText } from "./stepLineText.js";
import { copyText, readText, clipboardStepLines, type ClipboardLike } from "./processClipboard.js";

/** The header fields ProcessTree reads — satisfied by the engine ProcessModel,
 *  the wire `Process`, and a ProcessDecl alike (it renders off the WalkResult,
 *  not the raw steps). */
export interface ProcessTreeHeader {
  readonly ownerPath?: readonly string[];
  readonly name: string;
  readonly description?: string;
}

export interface ProcessTreeProps {
  readonly process: ProcessTreeHeader;
  readonly walkResult: WalkResult;
  readonly activeStepIndex: number | null;
  readonly expandedSubprocesses: ReadonlySet<NodeId>;
  readonly onSelectStepIndex: (i: number | null) => void;
  readonly onSelectModule?: (path: string) => void;
  readonly onSelectInterface?: (path: string) => void;
  readonly onBranchSelect: (nodeId: NodeId, label: string) => void;
  readonly onSubprocessToggle: (nodeId: NodeId, expanded: boolean) => void;
  /**
   * EXPLICIT branch picks (walkerState.branchSelection). The walk marks the
   * default arm `selected` too, so without this the picker cannot tell a
   * manual choice from the fallback — needed to render (and clear back to)
   * the "auto" entry.
   */
  readonly branchSelection?: ReadonlyMap<NodeId, string>;
  /** Optional dismiss action — surfaces a Clear link in the header. */
  readonly onClear?: () => void;
  /** Dispatch a click on an `archlang://` link in the process description. */
  readonly onArchlangNav?: (href: string) => void;
  /** Lookup: subprocess name → rendered-markdown description. */
  readonly subprocessDescriptions?: ReadonlyMap<string, string>;
  /** Owner-path → colour from the unified "colour by" layer. */
  readonly ownerColor?: (path: readonly string[]) => string | undefined;
  /** WYSIWYG step editing — the tree is the linear editing spine for nested
   *  control flow. Absent/read-only ⇒ no affordances (canEdit gate). */
  readonly stepEdit?: ProcessStepEditApi;
  /** Test/embedder override for the clipboard (Copy/Paste). Omitted ⇒ the real
   *  `navigator.clipboard`, feature-detected. */
  readonly clipboard?: ClipboardLike;
  /** SHARED selection lifted to ProcessMode so the tree + swimlane drive ONE
   *  selection (P5 #34). When present the tree consumes it and does NOT render
   *  its own toolbar (ProcessMode owns it, shared across surfaces); the matching
   *  `stepOrder` is the document-order axis a Shift-range walks. When absent the
   *  tree keeps its self-contained local selection + toolbar (standalone use). */
  readonly selection?: ProcessSelection;
}

export function ProcessTree({
  process,
  walkResult,
  activeStepIndex,
  expandedSubprocesses,
  onSelectStepIndex,
  onSelectModule,
  onSelectInterface,
  onBranchSelect,
  onSubprocessToggle,
  branchSelection,
  onClear,
  onArchlangNav,
  subprocessDescriptions,
  ownerColor,
  stepEdit,
  clipboard,
  selection: sharedSelection,
}: ProcessTreeProps): React.JSX.Element {
  const editable = stepEdit?.canEdit === true;
  // Selection is SHARED when ProcessMode lifts it (tree + swimlane drive one
  // selection, P5 #34); otherwise the tree owns a local one (standalone use).
  // The local hook always runs (rules of hooks) — a no-op when shared wins.
  const localSelection = useProcessSelection();
  const selection = sharedSelection ?? localSelection;
  // ProcessMode renders the toolbar when it owns the selection; the tree only
  // renders its own when it's the selection owner (no shared prop).
  const ownsToolbar = sharedSelection === undefined;
  // Document order of the selectable step nodeIds — the axis a Shift-range walks.
  // ALWAYS the tree's OWN present-path rows (from its walkResult), never the wider
  // shared all-branches order ProcessMode lifts for the swimlane: a tree
  // Shift-range must not pull in hidden steps of excluded branches (P5 #34.2).
  const order = React.useMemo(() => orderedStepIds(walkResult), [walkResult]);
  // A row's plain click keeps the single-select scrubber behaviour AND replaces
  // the multi-selection; ⌘/Shift only touch the selection (never the scrubber).
  const onRowSelect = React.useCallback(
    (nodeId: NodeId, e: React.MouseEvent, stepIndex: number): void => {
      if (!editable) {
        onSelectStepIndex(stepIndex);
        return;
      }
      const mods = clickModifiers(e);
      if (!mods.meta && !mods.shift) onSelectStepIndex(stepIndex);
      selection.onRowClick(nodeId, mods, order);
    },
    [editable, onSelectStepIndex, selection, order],
  );

  const { selected, count, clear } = selection;
  // Whole-block + multi-select drag (P2 #19). Draggable rows (leaf steps AND
  // container heads) register their DOM node; a threshold-gated body press drags
  // (a click still selects). Dropping a selected row moves the whole selection
  // (moveSteps, one undo); an unselected row / a block moves alone (moveStep, a
  // block relocates whole). Only wired in edit mode.
  const treeDrag = useTreeStepDrag({
    walk: walkResult,
    stepEdit: editable ? stepEdit : undefined,
    selected,
  });
  // nodeId → WalkStep, for the Copy text. Built once per walk (a Map keyed by the
  // step nodeId); a stale/absent id yields undefined and is skipped, never throws.
  const stepByNodeIdMap = React.useMemo(() => {
    const m = new Map<NodeId, import("@archlang/engine").WalkStep>();
    for (const s of walkResult.steps) m.set(s.nodeId, s);
    return m;
  }, [walkResult]);
  const stepByNodeId = React.useCallback((nodeId: NodeId) => stepByNodeIdMap.get(nodeId), [stepByNodeIdMap]);

  const onCopy = React.useCallback(() => {
    void copyText(selectedStepsText(order, selected, stepByNodeId), clipboard);
  }, [order, selected, stepByNodeId, clipboard]);

  const onDelete = React.useCallback(() => {
    if (!stepEdit) return;
    // Delete in DOCUMENT order (stable, deterministic); the api batches them
    // into ONE journaled mutation so a single undo brings them all back.
    const ids = order.filter((id) => selected.has(id));
    if (ids.length === 0) return;
    // Clear the selection ONLY when the batch actually applied — a refused /
    // stale delete keeps the rows selected (and the toolbar up) so the failure
    // isn't silently swallowed.
    if (stepEdit.deleteSteps(ids)) clear();
  }, [stepEdit, order, selected, clear]);

  // Paste lands after the LAST selected row (document order) — the natural
  // insertion point. Null when nothing is selected (disables the button).
  const pasteAnchor = React.useMemo<NodeId | null>(() => {
    let last: NodeId | null = null;
    for (const id of order) if (selected.has(id)) last = id;
    return last;
  }, [order, selected]);

  const onPaste = React.useCallback(() => {
    if (!stepEdit || !pasteAnchor) return;
    void (async () => {
      const text = await readText(clipboard);
      if (!text) return;
      const lines = clipboardStepLines(text);
      // Insert in order after the anchor: each line goes AFTER the previous
      // insert so the pasted block keeps its order. Re-resolve is unnecessary —
      // addStepAfter re-derives its stepId per call against the fresh walk, but a
      // synchronous loop hits the same `stale` guard as multi-delete, so we
      // insert the whole block as ONE multi-line text after the anchor.
      if (lines.length > 0) stepEdit.addStepAfter(pasteAnchor, lines.join("\n"));
    })();
  }, [stepEdit, pasteAnchor, clipboard]);

  return (
    <div className="p-4 overflow-y-auto space-y-4 h-full mx-auto w-full max-w-3xl">
      <header>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase text-slate-500 tracking-wide font-semibold">Process</span>
          {onClear && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onClear}
              className="uppercase tracking-wider"
            >
              Clear
            </Button>
          )}
        </div>
        <h2 className="text-base font-semibold text-white">
          {process.ownerPath && process.ownerPath.length > 0 && (
            <span className="text-slate-400 font-normal">
              {process.ownerPath.join(".")}
              <span className="text-slate-500">.</span>
            </span>
          )}
          {process.name}
        </h2>
        {process.description && (
          <div
            className="arch-desc text-xs text-slate-400 mt-1"
            onClick={
              onArchlangNav
                ? (e) => handleDescriptionClick(e, onArchlangNav)
                : undefined
            }
            dangerouslySetInnerHTML={{
              __html: renderDescriptionHtml(process.description),
            }}
          />
        )}
        <div className="text-[10px] text-slate-500 mt-1">
          {walkResult.steps.length} step{walkResult.steps.length === 1 ? "" : "s"}
        </div>
      </header>
      <ol className="space-y-1">
        {walkResult.tree.map((node, i) => (
          <NodeView
            key={nodeKey(node, i)}
            node={node}
            activeStepIndex={activeStepIndex}
            expandedSubprocesses={expandedSubprocesses}
            walkResult={walkResult}
            onSelectStepIndex={onSelectStepIndex}
            onSelectModule={onSelectModule}
            onSelectInterface={onSelectInterface}
            onBranchSelect={onBranchSelect}
            onSubprocessToggle={onSubprocessToggle}
            branchSelection={branchSelection}
            onArchlangNav={onArchlangNav}
            subprocessDescriptions={subprocessDescriptions}
            ownerColor={ownerColor}
            stepEdit={stepEdit}
            selection={editable ? selection : undefined}
            onRowSelect={onRowSelect}
            treeDrag={editable ? treeDrag : undefined}
          />
        ))}
      </ol>
      {editable && ownsToolbar && (
        <ProcessSelectionToolbar
          count={count}
          onDelete={onDelete}
          onCopy={onCopy}
          onPaste={pasteAnchor ? onPaste : null}
          onClear={clear}
        />
      )}
    </div>
  );
}

function nodeKey(node: WalkNode, fallback: number): string {
  return "nodeId" in node ? node.nodeId : `idx:${fallback}`;
}

/** True when a pointerdown originated on an interactive control (a button /
 *  select / input / link / anything opted out via `[data-no-drag]`) — pressing
 *  those must NOT arm a row drag (they own their own gesture). */
function isInteractiveTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el?.closest?.("button, a, select, input, textarea, [data-no-drag]");
}

/** The prop bag spread on a draggable row element: standard HTML attributes, a
 *  ref callback, plus the `data-*` drag markers (data attrs aren't in
 *  `HTMLAttributes`, so widen with a template-keyed record). */
type RowDragProps = React.HTMLAttributes<HTMLElement> & {
  ref?: (el: HTMLElement | null) => void;
  [dataAttr: `data-${string}`]: string | undefined;
};

/**
 * The drag wiring for ONE draggable tree row (P2 #19). Returns the props to
 * spread on the row element — a `ref` (registers the row's rect for hit-testing),
 * an `onPointerDown` that ARMS the threshold-gated body drag (guarded against
 * interactive children), a `data-drag-row` marker + `data-dragging` when it's the
 * one being dragged — plus the live drop-indicator side (a top/bottom insertion
 * line vs this row). Selection stays on the element's own `onClick`; a real drag
 * swallows the trailing click so it never also selects. When `treeDrag` is absent
 * (view mode) it returns just `{}` so the row is inert.
 */
function rowDragBinding(
  treeDrag: TreeDragApi | undefined,
  nodeId: NodeId,
): {
  readonly props: RowDragProps;
  readonly dragging: boolean;
  readonly dropEdge: "before" | "after" | null;
} {
  if (!treeDrag) return { props: {}, dragging: false, dropEdge: null };
  const dragging = treeDrag.draggingNodeId === nodeId;
  const t = treeDrag.target;
  const dropEdge =
    t && t.kind === "reorder" && t.anchorNodeId === nodeId ? t.position : null;
  return {
    props: {
      ref: treeDrag.registerRow(nodeId),
      "data-drag-row": nodeId,
      ...(dragging ? { "data-dragging": "true" } : {}),
      onPointerDown: (e: React.PointerEvent) => {
        if (isInteractiveTarget(e.target)) return; // let the control handle it
        treeDrag.onRowBodyDown(nodeId, e);
      },
    },
    dragging,
    dropEdge,
  };
}

/** The insertion line drawn above/below a row that is the current drop anchor. */
function DropLine({ edge }: { edge: "before" | "after" }): React.JSX.Element {
  return (
    <div
      data-tree-drop-line={edge}
      aria-hidden="true"
      className="pointer-events-none absolute left-0 right-0 h-0.5 bg-sky-400"
      style={{ [edge === "before" ? "top" : "bottom"]: -1 }}
    />
  );
}

interface NodeViewProps {
  readonly node: WalkNode;
  readonly activeStepIndex: number | null;
  readonly expandedSubprocesses: ReadonlySet<NodeId>;
  readonly walkResult: WalkResult;
  readonly onSelectStepIndex: (i: number | null) => void;
  readonly onSelectModule?: (path: string) => void;
  readonly onSelectInterface?: (path: string) => void;
  readonly onBranchSelect: (nodeId: NodeId, label: string) => void;
  readonly onSubprocessToggle: (nodeId: NodeId, expanded: boolean) => void;
  /** Explicit branch picks — lets the picker show/clear the "auto" entry. */
  readonly branchSelection?: ReadonlyMap<NodeId, string>;
  readonly onArchlangNav?: (href: string) => void;
  readonly subprocessDescriptions?: ReadonlyMap<string, string>;
  readonly ownerColor?: (path: readonly string[]) => string | undefined;
  readonly stepEdit?: ProcessStepEditApi;
  /** Multi-select model — present only in edit mode. StepRow highlights when a
   *  row is in it; absent ⇒ no multi-select (view mode). */
  readonly selection?: ProcessSelection;
  /** Row-click handler that reconciles single-select (scrubber) with the
   *  multi-selection (⌘/Shift). Passed the row's nodeId, the event, and its
   *  stepIndex. */
  readonly onRowSelect?: (nodeId: NodeId, e: React.MouseEvent, stepIndex: number) => void;
  /** Whole-block + multi-select drag session (P2 #19) — present only in edit
   *  mode. Draggable rows register their element + arm a body drag through it. */
  readonly treeDrag?: TreeDragApi;
}

function NodeView(props: NodeViewProps): React.JSX.Element {
  const { node } = props;
  switch (node.kind) {
    case "step":
      return <StepRow {...props} step={node} />;
    case "each":
      return (
        <ContainerBlock
          glyph={node.source ? "≡" : "↻"}
          label={`each ${node.label ?? ""}${node.source ? ` in ${node.source}` : ""}`}
          owner={node.owner}
          blockNodeId={node.nodeId}
          morphCaps={blockMorphCaps(node)}
          {...props}
          children={node.children}
        />
      );
    case "parallel":
      return (
        <ContainerBlock
          glyph="+"
          label={`parallel${node.mergeAspect ? ` ${node.mergeAspect}` : ""}`}
          owner={node.owner}
          blockNodeId={node.nodeId}
          morphCaps={blockMorphCaps(node)}
          mergeCaps={mergeCapsFor(node)}
          selectCountCaps={selectCountCapsFor(node)}
          awaitBoundary={isAwaitBoundaryParallel(node)}
          {...props}
          children={node.branches.flatMap((b) => b.children)}
        />
      );
    case "if":
      return <BranchedBlock kind="if" glyph="×" owner={node.owner} nodeId={node.nodeId} branches={[...node.branches, ...(node.elseBranch ? [node.elseBranch] : [])]} editField="condition" armCount={node.branches.length} {...props} />;
    case "switch":
      return <BranchedBlock kind="switch" glyph="◯" owner={node.owner} nodeId={node.nodeId} head={node.head} branches={node.cases} runCount={node.runCount} editField="case" {...props} />;
    case "try":
      return <BranchedBlock kind="try" glyph="⚡" owner={node.owner} nodeId={node.nodeId} branches={[node.tryBranch, ...node.catches]} {...props} />;
    case "do":
      return <DoBlock {...props} node={node} />;
    case "terminator":
      return <TerminatorRow type={node.type} reason={node.reason} all={node.all} />;
  }
}

/** Construct heading chip: BPMN glyph + label, tinted by the construct owner.
 *  In edit mode it doubles as the block's DRAG GRIP (P2 #19) — `dragProps`
 *  (ref + threshold body-press) spread on its root, a grab cursor, a
 *  dragging-dim, and the drop insertion line when it is the anchor. */
function ConstructHeading({
  glyph,
  label,
  tint,
  children,
  dragProps,
  dropEdge,
  dragging,
}: {
  glyph: string;
  label: React.ReactNode;
  tint?: string;
  children?: React.ReactNode;
  dragProps?: RowDragProps;
  dropEdge?: "before" | "after" | null;
  dragging?: boolean;
}) {
  return (
    <div
      {...dragProps}
      className={
        "relative text-[11px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2" +
        (dragProps ? " cursor-grab" : "") +
        (dragging ? " opacity-40" : "")
      }
    >
      {dropEdge && <DropLine edge={dropEdge} />}
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center w-4 h-4 rounded-sm border text-[10px] normal-case shrink-0"
        style={{
          borderColor: tint ?? "rgb(51 65 85)", // slate-700
          color: tint ?? "rgb(148 163 184)", // slate-400
        }}
      >
        {glyph}
      </span>
      <span style={tint ? { color: tint } : undefined}>{label}</span>
      {children}
    </div>
  );
}

interface ChildrenProps extends Omit<NodeViewProps, "node"> {
  readonly children: readonly WalkNode[];
  readonly label?: string;
  readonly glyph?: string;
  readonly owner?: QualifiedName;
  readonly blockNodeId?: NodeId;
  /** Block-restructure gating (unwrap / morph) for this container, computed from
   *  its walk node — only present for an unwrappable/morphable container. */
  readonly morphCaps?: BlockMorphCaps;
  /** Merge-behaviour gating (parallel join/race/out/count) — present on plain
   *  parallels (P3 #23). */
  readonly mergeCaps?: MergeCaps;
  /** Inclusive run-count gating (`parallel select` / top-level select). */
  readonly selectCountCaps?: SelectCountCaps;
  /** True when this container is a `parallel await` boundary — its head is
   *  await-target editable. */
  readonly awaitBoundary?: boolean;
}

function ContainerBlock({ label, glyph, owner, children, blockNodeId, morphCaps, mergeCaps, selectCountCaps, awaitBoundary, ...rest }: ChildrenProps): React.JSX.Element {
  const tint = owner ? rest.ownerColor?.(owner) : undefined;
  // The head is the block's drag grip (P2 #19): a whole-block drag relocates the
  // header + body + `}` as a unit (moveStep on the block nodeId). Block heads
  // aren't multi-select rows, so the body-click is a no-op.
  const drag = rowDragBinding(blockNodeId ? rest.treeDrag : undefined, blockNodeId ?? ("" as NodeId));
  return (
    <li
      className="border-l-2 pl-3 my-2"
      style={{ borderColor: tint ?? "rgb(51 65 85)" }}
      data-owner-colored={tint ? true : undefined}
    >
      <ConstructHeading glyph={glyph ?? "▸"} label={label ?? ""} tint={tint} dragProps={drag.props} dropEdge={drag.dropEdge} dragging={drag.dragging}>
        {rest.stepEdit && blockNodeId && (
          <StepEditMenu
            api={rest.stepEdit}
            nodeId={blockNodeId}
            kind="block"
            canUnwrap={morphCaps?.canUnwrap}
            morphTargets={morphCaps?.morphTargets}
            {...(mergeCaps?.canSetMerge ? { merge: { canSet: true, ...(mergeCaps.current ? { current: mergeCaps.current } : {}) } } : {})}
            {...(selectCountCaps?.canSetCount ? { selectCount: { parallel: selectCountCaps.parallel, ...(selectCountCaps.runCount !== undefined ? { runCount: selectCountCaps.runCount } : {}) } } : {})}
            {...(awaitBoundary ? { awaitBoundary: true } : {})}
          />
        )}
      </ConstructHeading>
      <ol className="space-y-1">
        {children.map((c, i) => (
          // Spread first, then `node` — callers spread the parent's
          // NodeViewProps into us which includes the parent's `node`.
          // Without an explicit override at the END the child's `node`
          // gets clobbered and renders the parent again → infinite loop.
          <NodeView key={nodeKey(c, i)} {...rest} node={c} />
        ))}
      </ol>
    </li>
  );
}

/** One branch/case label line. Single-click still picks (exclusive frame,
 *  unselected); when `onEdit` is present a double-click inline-edits the
 *  condition / case-label span (the same funnel as the callee/terminator edits).
 *  Its own context menu (pick / copy) so the parent block stays menu-free. */
function EditableBranchLabel({
  branch,
  pickable,
  tint,
  onPick,
  onEdit,
  onRemove,
}: {
  readonly branch: WalkBranch;
  readonly pickable: boolean;
  readonly tint?: string;
  readonly onPick: () => void;
  readonly onEdit?: (text: string) => void;
  /** Present only on a REMOVABLE arm (an `else`/`else if`/`catch`, never the
   *  primary `if` / `try` body) — renders a hover "×" that drops just that arm. */
  readonly onRemove?: () => void;
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const menu = useContextMenu();
  if (editing && onEdit) {
    return (
      <InlineEditInput
        initial={branch.label}
        ariaLabel="Edit branch label"
        onCommit={(v) => {
          onEdit(v);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        className="w-full box-border rounded-sm border border-sky-500 bg-slate-900 px-1 py-0.5 text-[10px] text-slate-100 outline-none"
      />
    );
  }
  const editProps = onEdit
    ? { onDoubleClick: () => setEditing(true), "data-branch-editable": "", title: "Double-click to edit" }
    : {};
  // A hover "×" that drops this arm (contextual — the block's ⋯ menu can't tell
  // which arm the pointer is on). Rendered inside a `group/arm` so it reveals on
  // arm-row hover only. Right-click also offers "Remove arm".
  const removeBtn = onRemove ? (
    <IconButton
      variant="ghost"
      size="xs"
      data-arm-remove={branch.label || "else"}
      aria-label="Remove this arm"
      title="Remove this arm"
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      className="opacity-0 group-hover/arm:opacity-100 focus-visible:opacity-100 ml-1 text-[10px] leading-none hover:bg-rose-500/20 hover:text-rose-300"
    >
      ×
    </IconButton>
  ) : null;
  const ctxItems = (): ContextMenuItem[] => [
    ...(pickable ? [{ label: "Pick branch", onClick: onPick }] : []),
    { label: "Copy label", onClick: () => { void navigator.clipboard?.writeText(branch.label); } },
    ...(onRemove ? [{ label: "Remove arm", separator: true, onClick: onRemove }] : []),
  ];
  if (pickable && !branch.selected) {
    return (
      <span className="group/arm inline-flex items-center">
        <Tooltip content={onEdit ? "Show this branch · double-click to edit" : "Show this branch"}>
          <button
            type="button"
            data-branch-pick={branch.label}
            onClick={onPick}
            onContextMenu={(e) => menu.open(e, ctxItems())}
            {...editProps}
            className="text-[10px] text-slate-500 hover:text-sky-300 cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            {"○ "}
            {branch.label}
          </button>
        </Tooltip>
        {removeBtn}
        {menu.node}
      </span>
    );
  }
  return (
    <div className="group/arm inline-flex items-center" onContextMenu={onRemove ? (e) => menu.open(e, ctxItems()) : undefined}>
      <span
        {...editProps}
        className="text-[10px]"
        style={{ color: branch.selected ? tint ?? "rgb(52 211 153)" : "rgb(100 116 139)" }}
      >
        {branch.selected ? "● " : "○ "}
        {branch.label}
      </span>
      {removeBtn}
      {onRemove && menu.node}
    </div>
  );
}

interface BranchedBlockProps extends Omit<NodeViewProps, "node"> {
  readonly kind: "if" | "switch" | "try";
  readonly glyph: string;
  readonly owner?: QualifiedName;
  readonly nodeId: NodeId;
  readonly head?: string;
  readonly branches: readonly WalkBranch[];
  /** Inclusive-select runtime bound (`select N`/`one`) — annotates "N of M run".
   *  Undefined for bare select (all run) and for if/try. */
  readonly runCount?: number;
  /** Which span a branch LABEL inline-edits to (double-click): `condition` for
   *  `if`/`else if` arms, `case` for `select` cases. Undefined = not editable
   *  (a `try`'s catch labels are a separate op). */
  readonly editField?: "condition" | "case";
  /** For `editField: "condition"`: the count of condition arms (`if` + each
   *  `else if`). A branch at index < this — and with a non-empty label — is an
   *  editable condition; the trailing `else` (index ≥ this) is not. */
  readonly armCount?: number;
}

function BranchedBlock(props: BranchedBlockProps): React.JSX.Element {
  // Pull THIS block's own fields out of `rest` so they are NOT spread into
  // child NodeViews below. Otherwise a nested branched child (e.g. an `if`
  // inside a `switch` case) receives the parent's `branches`/`kind`/`nodeId`
  // via `{...rest}` — which, spread AFTER the child's explicit props in
  // NodeView, clobbers them back to the parent's and re-renders this same
  // block forever (infinite recursion → OOM). Mirrors ContainerBlock.
  const { kind, glyph, owner, nodeId, head, branches, runCount, editField, armCount, ...rest } = props;
  const onBranchSelect = rest.onBranchSelect;
  const stepEdit = rest.stepEdit;
  const tint = owner ? rest.ownerColor?.(owner) : undefined;
  // The picker's value is the EXPLICIT pick only — the walk marks the default
  // arm `selected` too, so "auto" (empty value, clears the pick) is shown
  // unless the user chose. The dots below still reflect the live path.
  const explicit = rest.branchSelection?.get(nodeId) ?? "";
  const defaultLabel = branches[0]?.label ?? "";
  // Only exclusive frames (if/try) expose a branch picker. An inclusive
  // `select` (switch) runs every matching case (PROCESS.md §3) and the walker
  // ignores `branchSelection` for it — so a picker there is inert and falsely
  // signals exclusivity. Show the head label alone for inclusive frames.
  const pickable = isFrameExclusive(kind);
  // Inline-edit the span behind a branch LABEL (double-click): an `if`/`else if`
  // arm's condition, or a `select` case's label. A body-only draft arm (empty
  // label) and the trailing `else` (index ≥ armCount) carry no span → no editor.
  const [editingHead, setEditingHead] = React.useState(false);
  const editLabel = (index: number): ((text: string) => void) | undefined => {
    if (!stepEdit?.canEdit || !editField) return undefined;
    if (editField === "case") return (text) => void stepEdit.setCaseLabel(nodeId, index, text);
    if (index >= (armCount ?? 0) || branches[index]?.label === "") return undefined;
    return (text) => void stepEdit.setBranchCondition(nodeId, index, text);
  };
  // Which arm each row can be REMOVED as (P3 #22) — `else`/`else if` on an `if`,
  // a `catch` on a `try`; never the primary `if` arm (index 0) or the `try` body
  // (index 0), never a `switch` case (a separate op). Presence gates the "×".
  const removeArmFor = (index: number): (() => void) | undefined => {
    if (!stepEdit?.canEdit) return undefined;
    let arm: RemovableArm | undefined;
    if (kind === "if") {
      // branches = [if, ...else-if, (else)]; armCount = if + else-if count.
      const ac = armCount ?? branches.length;
      if (index === 0) arm = undefined; // primary `if` — not removable this way.
      else if (index < ac) arm = { kind: "elseIf", index };
      else arm = { kind: "else" }; // trailing else (index ≥ armCount).
    } else if (kind === "try") {
      // branches = [tryBody, ...catches]; catch index is offset by 1.
      if (index >= 1) arm = { kind: "catch", index: index - 1 };
    }
    if (!arm) return undefined;
    const a = arm;
    return () => void stepEdit.removeArm(nodeId, a);
  };
  const headEditable = kind === "switch" && !!head && !!stepEdit?.canEdit;
  const heading: React.ReactNode =
    kind === "switch" && head ? (
      headEditable && editingHead ? (
        <InlineEditInput
          initial={head}
          ariaLabel="Edit select head"
          onCommit={(v) => { stepEdit!.setSelectHead(nodeId, v); setEditingHead(false); }}
          onCancel={() => setEditingHead(false)}
          className="w-40 box-border rounded-sm border border-sky-500 bg-slate-900 px-1 py-0.5 text-[11px] text-slate-100 outline-none"
        />
      ) : (
        <span>
          {"switch "}
          <span
            {...(headEditable ? { onDoubleClick: () => setEditingHead(true), "data-select-head": "", title: "Double-click to edit" } : {})}
            className={headEditable ? "underline decoration-dotted underline-offset-2 cursor-text" : undefined}
          >
            {head}
          </span>
        </span>
      )
    ) : (
      kind
    );
  // Inclusive `select N`/`one` runs N of the M cases at runtime (WHICH is not
  // author-chosen) — annotate the bound; all cases stay live (no dimming).
  const runAnnotation =
    kind === "switch" && runCount !== undefined && runCount < branches.length
      ? `${runCount} of ${branches.length} run`
      : undefined;
  // The head is the block's drag grip (P2 #19) — a whole if/switch/try relocates
  // as a unit. Block heads aren't multi-select rows (no selection on a head).
  const drag = rowDragBinding(rest.treeDrag, nodeId);
  return (
    <>
    <li
      className="border-l-2 pl-3 my-2"
      style={{ borderColor: tint ?? "rgb(51 65 85)" }}
      data-owner-colored={tint ? true : undefined}
    >
      <ConstructHeading glyph={glyph} label={heading} tint={tint} dragProps={drag.props} dropEdge={drag.dropEdge} dragging={drag.dragging}>
        {rest.stepEdit && (
          <StepEditMenu
            api={rest.stepEdit}
            nodeId={nodeId}
            kind="block"
            blockConstruct={kind === "if" ? "if" : kind === "try" ? "try" : undefined}
            canAddElse={kind === "if" && armCount !== undefined && branches.length <= armCount}
            // Unwrap gating (mirrors processBlockMorph): a single-arm `if` with no
            // else (branches == armCount == 1); a `try` with no catch (only the
            // try branch present). A `switch` never unwraps (cases aren't a flow).
            canUnwrap={
              kind === "if"
                ? branches.length === 1 && armCount === 1
                : kind === "try"
                  ? branches.length === 1
                  : false
            }
            // A top-level `select` (switch) offers the inclusive run-count edit
            // (all/one/N) — P3 #23. `if`/`try` do not.
            {...(kind === "switch" ? { selectCount: { parallel: false, ...(runCount !== undefined ? { runCount } : {}) } } : {})}
          />
        )}
        {runAnnotation && <span className="text-slate-400 normal-case tracking-normal">{runAnnotation}</span>}
        {pickable && (
          <select
            className="bg-slate-900 border border-slate-700 text-slate-200 text-[11px] rounded-sm px-1 py-0.5"
            value={explicit}
            onChange={(e) => onBranchSelect(nodeId, e.target.value)}
          >
            {/* Empty value = clear the manual pick (walker default arm). */}
            <option value="">auto{defaultLabel ? ` (${defaultLabel})` : ""}</option>
            {branches.map((b) => (
              <option key={b.label} value={b.label}>{b.label}</option>
            ))}
          </select>
        )}
      </ConstructHeading>
      <ol className="space-y-1">
        {branches.map((b, i) => (
          <li key={`br-${i}`} className={b.selected ? "" : "opacity-40"}>
            <EditableBranchLabel
              branch={b}
              pickable={pickable}
              tint={tint}
              onPick={() => onBranchSelect(nodeId, b.label)}
              onEdit={editLabel(i)}
              onRemove={removeArmFor(i)}
            />
            {b.selected && (
              <ol className="space-y-1 pl-3">
                {b.children.map((c, ci) => (
                  // Spread `rest` (this block's own branches/kind/nodeId
                  // already stripped out), then `node={c}` last so neither the
                  // child's node nor its branched-block props get clobbered.
                  <NodeView key={nodeKey(c, ci)} {...rest} node={c} />
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
    </li>
    </>
  );
}

interface DoBlockProps extends Omit<NodeViewProps, "node"> {
  readonly node: Extract<WalkNode, { kind: "do" }>;
}

function DoBlock({ node, expandedSubprocesses, walkResult, onSubprocessToggle, ...rest }: DoBlockProps): React.JSX.Element {
  const menu = useContextMenu();
  const isExpanded = expandedSubprocesses.has(node.nodeId);
  const range = walkResult.collapsedGroups.get(node.nodeId);
  const argSuffix = node.args && node.args.length ? `(${node.args.join(", ")})` : "()";
  const description = rest.subprocessDescriptions?.get(node.target);
  return (
    <>
    <li className="border-l-2 border-slate-700 pl-3 my-2">
      <button
        onClick={() => onSubprocessToggle(node.nodeId, !isExpanded)}
        onContextMenu={(e) =>
          menu.open(e, [
            {
              label: isExpanded ? "Collapse" : "Expand",
              disabled: node.recursive,
              onClick: () => onSubprocessToggle(node.nodeId, !isExpanded),
            },
            { label: "Copy name", onClick: () => { void navigator.clipboard?.writeText(node.target); } },
          ])
        }
        className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        disabled={node.recursive}
      >
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-4 h-4 rounded-sm border border-slate-700 text-slate-400 text-[10px] shrink-0"
        >
          ⊞
        </span>
        {node.recursive ? "↻" : isExpanded ? "▾" : "▸"} {node.target}{argSuffix}
        {node.recursive && <span className="ml-2 text-amber-400">(recursive)</span>}
      </button>
      {rest.stepEdit && <StepEditMenu api={rest.stepEdit} nodeId={node.nodeId} kind="other" className="ml-1" />}
      {node.comment && (
        <span className="text-slate-500 italic ml-2 text-xs" title={node.comment}>“{node.comment}”</span>
      )}
      {isExpanded && description && (
        <div
          className="arch-desc text-[11px] text-slate-400 pl-4 mt-1"
          onClick={
            rest.onArchlangNav
              ? (e) => handleDescriptionClick(e, rest.onArchlangNav!)
              : undefined
          }
          dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(description) }}
        />
      )}
      {!isExpanded && range && (
        <div className="text-[10px] text-slate-500 pl-4 mt-1">
          (collapsed — {range.endIndex - range.startIndex + 1} inner steps, indices {range.startIndex}…{range.endIndex})
        </div>
      )}
      {isExpanded && node.body && (
        <ol className="space-y-1 pl-3 mt-1">
          {node.body.map((c, i) => (
            // Same node-override bug as ContainerBlock: rest first, then
            // node={c} last so it isn't clobbered by a parent's `node`
            // leaking through the spread.
            <NodeView
              key={nodeKey(c, i)}
              {...rest}
              expandedSubprocesses={expandedSubprocesses}
              walkResult={walkResult}
              onSubprocessToggle={onSubprocessToggle}
              node={c}
            />
          ))}
        </ol>
      )}
    </li>
    {menu.node}
    </>
  );
}

/** Inline `unwind` (compensation) editor for a `reversible` forward step (P3
 *  #22). Shows the current compensating action (`⟲ > Inventory.release`), a
 *  double-click / click-to-edit into an InlineEditInput → `setUnwind`, and a "×"
 *  to remove it; a step with no unwind gets a hover "+ unwind" to add one. The
 *  text is the compensating ACTION (saga rule: this step's OWN compensation) —
 *  the engine renders `unwind <text>` and refuses a non-reversible step. */
function UnwindEditor({
  nodeId,
  unwindText,
  stepEdit,
}: {
  readonly nodeId: NodeId;
  /** Current paired unwind action text, or undefined when the item has none. */
  readonly unwindText?: string;
  readonly stepEdit: ProcessStepEditApi;
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  if (editing) {
    return (
      <span className="shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
        <InlineEditInput
          initial={unwindText ?? ""}
          placeholder="> Callee.compensate"
          ariaLabel="Edit unwind compensation"
          onCommit={(v) => {
            stepEdit.setUnwind(nodeId, v.trim());
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
          className="w-44 box-border rounded-sm border border-amber-500 bg-slate-950 px-1 py-0.5 text-[11px] text-amber-100 outline-none"
        />
      </span>
    );
  }
  if (unwindText !== undefined) {
    return (
      <span className="group/unwind shrink-0 ml-2 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {/* Amber is the compensation semantic (PROCESS.md §8) — no kit variant
            carries it, so ghost supplies the box/ring and the paint stays here. */}
        <Button
          variant="ghost"
          size="xs"
          data-unwind-edit=""
          title="Compensation (unwind) — double-click to edit"
          onClick={() => setEditing(true)}
          onDoubleClick={() => setEditing(true)}
          className="gap-1 border border-amber-500/40 bg-amber-500/10 px-1 text-[10px] text-amber-300 hover:border-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
        >
          <span aria-hidden="true">⟲</span>
          <span className="font-mono">{unwindText}</span>
        </Button>
        <IconButton
          variant="ghost"
          size="xs"
          data-unwind-remove=""
          aria-label="Remove unwind"
          title="Remove compensation"
          onClick={() => stepEdit.setUnwind(nodeId, "")}
          className="opacity-0 group-hover/unwind:opacity-100 focus-visible:opacity-100 text-[10px] leading-none hover:bg-rose-500/20 hover:text-rose-300"
        >
          ×
        </IconButton>
      </span>
    );
  }
  return (
    <Button
      variant="ghost"
      size="xs"
      data-unwind-add=""
      title="Add a compensation (unwind) for this step"
      aria-label="Add unwind"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className="opacity-0 group-hover/step:opacity-100 focus-visible:opacity-100 shrink-0 ml-2 border border-dashed border-amber-500/40 px-1 text-[10px] leading-none text-amber-400/80 hover:border-amber-400 hover:bg-transparent hover:text-amber-300"
    >
      + unwind
    </Button>
  );
}

interface StepRowProps extends NodeViewProps {
  readonly step: Extract<WalkNode, { kind: "step" }>;
}

function StepRow({ step, activeStepIndex, walkResult, onSelectStepIndex, onSelectModule, onSelectInterface, ownerColor, stepEdit, selection, onRowSelect, treeDrag }: StepRowProps): React.JSX.Element {
  const menu = useContextMenu();
  // Row-hover one-click insert (plan §5 P3): a "+" reveals on hover and opens an
  // inline step input → addStepAfter, the tree peer of the board connector "+".
  const [adding, setAdding] = React.useState(false);
  // Inline-edit of a standalone `await <head>` step's target (P3 #23) — a note-
  // shaped step whose nodeId marks it await. Double-click → InlineEditInput →
  // setAwaitTarget (the head only; the engine rewrites just the head span).
  const [editingAwait, setEditingAwait] = React.useState(false);
  const fromPath = step.from.join(".");
  const toPath = step.to.join(".");
  const isAwait = step.noteText !== undefined && isAwaitNodeId(step.nodeId);
  const awaitEditable = isAwait && !!stepEdit?.canEdit;
  // The displayed note text for an await is `await <head>`; the edit target is
  // just `<head>` (strip the leading keyword for the input prefill).
  const awaitHead = isAwait ? step.noteText!.replace(/^await\s+/, "") : "";
  // A real edge (from > to, not prose/note) is a `call`; owner/callee edits
  // apply there. Prose/note steps are `other` (add/delete/wrap only).
  const editKind: "call" | "other" = step.noteText === undefined && fromPath !== "" && toPath !== "" ? "call" : "other";
  const isActive = activeStepIndex === step.stepIndex;
  // Multi-selection membership (edit mode only). The active-step (scrubber) glow
  // wins visually when a row is both; a selected-but-not-active row gets its own
  // sky ring so a Shift/⌘ selection reads as a set even before it's the anchor.
  const isSelected = selection?.has(step.nodeId) === true;
  const isComp = step.modifiers?.compensation === true;
  // v0.9.0 §2.1: a leading await is one of the process's start triggers.
  const isTrigger = walkResult.triggers?.includes(step.nodeId) === true;
  // "Colour by" tint of the CALLER's module — matches the BPMN owner chip.
  const tint = !step.prose && step.from.length > 0 ? ownerColor?.(step.from) : undefined;
  const ownerDot = tint ? (
    <span
      aria-hidden="true"
      data-owner-dot
      className="inline-block w-2 h-2 rounded-full shrink-0 mr-1"
      style={{ background: tint }}
    />
  ) : null;
  // A note step is an owned action with text and no edge; flow prose is the
  // ownerless positional variant (v0.9.0 §2.6) — neither has an arrow to draw.
  const body = step.noteText !== undefined ? (
    <span className="flex-1 min-w-0" {...(step.prose ? { "data-prose": "true" } : {})}>
      {!step.prose && fromPath && (
        <>
          {ownerDot}
          <button
            onClick={(e) => { e.stopPropagation(); onSelectModule?.(fromPath); }}
            onDoubleClick={(e) => { e.stopPropagation(); onSelectInterface?.(fromPath); }}
            className="hover:underline underline-offset-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            style={tint ? { color: tint } : undefined}
          >{fromPath}</button>
          <span className="text-slate-500 mx-1" />
        </>
      )}
      {awaitEditable && editingAwait ? (
        <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <span className="text-slate-400 italic">await </span>
          <InlineEditInput
            initial={awaitHead}
            ariaLabel="Edit await head"
            onCommit={(v) => { stepEdit!.setAwaitTarget(step.nodeId, v); setEditingAwait(false); }}
            onCancel={() => setEditingAwait(false)}
            className="w-32 box-border rounded-sm border border-sky-500 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-100 outline-none"
          />
        </span>
      ) : (
        <span
          className={"text-slate-400 italic" + (awaitEditable ? " underline decoration-dotted underline-offset-2 cursor-text" : "")}
          {...(awaitEditable ? { onDoubleClick: (e: React.MouseEvent) => { e.stopPropagation(); setEditingAwait(true); }, "data-await-target": "", title: "Double-click to edit the wait" } : {})}
        >
          {step.noteText}
        </span>
      )}
    </span>
  ) : (
    <span className="flex-1 min-w-0">
      {ownerDot}
      <button
        onClick={(e) => { e.stopPropagation(); onSelectModule?.(fromPath); }}
        onDoubleClick={(e) => { e.stopPropagation(); onSelectInterface?.(fromPath); }}
        className={cx("hover:underline underline-offset-2 rounded-sm", FOCUS_RING)}
        style={tint ? { color: tint } : undefined}
      >{fromPath}</button>
      <span className="text-slate-500 mx-1">→</span>
      <button
        onClick={(e) => { e.stopPropagation(); onSelectModule?.(toPath); }}
        onDoubleClick={(e) => { e.stopPropagation(); onSelectInterface?.(toPath); }}
        className={cx("hover:underline underline-offset-2 rounded-sm", FOCUS_RING)}
      >{toPath}</button>
      {step.bind && <span className="text-slate-500 ml-2">= {step.bind}</span>}
      {step.comment && (
        <span className="text-slate-500 italic ml-2" title={step.comment}>“{step.comment}”</span>
      )}
    </span>
  );
  // Human-readable one-line label for this step (matches what the row shows).
  const stepLabel =
    step.noteText !== undefined
      ? fromPath
        ? `${fromPath}: ${step.noteText}`
        : step.noteText
      : `${fromPath} → ${toPath}`;
  const onMenu = (e: React.MouseEvent): void => {
    const items: ContextMenuItem[] = [
      {
        label: "Jump to diagram",
        onClick: () => onSelectStepIndex(step.stepIndex),
      },
      {
        label: "Copy step label",
        separator: true,
        onClick: () => { void navigator.clipboard?.writeText(stepLabel); },
      },
      {
        label: "Copy step id",
        onClick: () => { void navigator.clipboard?.writeText(step.nodeId); },
      },
    ];
    menu.open(e, items);
  };
  // Whole-block/multi drag binding (edit mode). The BODY press arms the drag;
  // selection stays on the native onClick (a real drag swallows the trailing
  // click so it doesn't also select).
  const drag = rowDragBinding(treeDrag, step.nodeId);
  return (
    <>
    <li
      {...drag.props}
      {...(isTrigger ? { "data-trigger": "true" } : {})}
      {...(isSelected ? { "data-selected": "true" } : {})}
      aria-selected={selection ? isSelected : undefined}
      title={isTrigger ? "start trigger — the process begins when this fires" : undefined}
      className={
        "group/step relative flex items-start gap-2 text-xs py-1 cursor-pointer rounded-sm px-1 " +
        (drag.dragging ? "opacity-40 " : "") +
        (isActive
          ? "bg-sky-500/25 text-sky-100 ring-1 ring-sky-500/60"
          : isSelected
            ? "bg-sky-500/15 text-sky-100 ring-1 ring-sky-500/40"
            : "text-slate-300 hover:bg-slate-800/(--tint-muted)")
      }
      onClick={(e) => (onRowSelect ? onRowSelect(step.nodeId, e, step.stepIndex) : onSelectStepIndex(step.stepIndex))}
      onContextMenu={onMenu}
    >
      {drag.dropEdge && <DropLine edge={drag.dropEdge} />}
      <span
        className={
          "rounded-full text-white font-semibold w-5 h-5 flex items-center justify-center text-[11px] shrink-0 " +
          (isComp ? "bg-amber-500" : "bg-sky-500")
        }
      >
        {displayLabelFor(walkResult, step.stepIndex, isComp)}
      </span>
      {isTrigger && <span className="text-emerald-300 shrink-0 text-[10px] pt-0.5">▶</span>}
      {body}
      {/* Reversible forward step (P3 #22): inline unwind / compensation editor.
          Only a forward saga step is `reversibleForward`; a compensation step
          (the reverse action) is not, so it never shows this. */}
      {stepEdit?.canEdit && step.modifiers.reversibleForward && (
        <UnwindEditor nodeId={step.nodeId} unwindText={step.modifiers.unwindText} stepEdit={stepEdit} />
      )}
      {stepEdit?.canEdit &&
        (adding ? (
          <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <InlineEditInput
              initial=""
              placeholder="A > B"
              ariaLabel="Add step after"
              onCommit={(v) => {
                stepEdit.addStepAfter(step.nodeId, v);
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
              className="w-32 box-border rounded-sm border border-sky-500 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-100 outline-none"
            />
          </span>
        ) : (
          <IconButton
            variant="ghost"
            size="xs"
            data-tree-add=""
            title="Add step after"
            aria-label="Add step after"
            onClick={(e) => {
              e.stopPropagation();
              setAdding(true);
            }}
            className="opacity-0 group-hover/step:opacity-100 focus-within:opacity-100 shrink-0 text-xs leading-none hover:text-sky-200"
          >
            +
          </IconButton>
        ))}
      {stepEdit && (
        <StepEditMenu
          api={stepEdit}
          nodeId={step.nodeId}
          kind={editKind}
          stepIndex={step.stepIndex}
          from={editKind === "call" ? fromPath : undefined}
          to={editKind === "call" ? toPath : undefined}
          {...(editKind === "call"
            ? { modifiers: { ...(step.asName ? { asName: step.asName } : {}), ...(step.comment ? { comment: step.comment } : {}) } }
            : {})}
          className="opacity-0 group-hover/step:opacity-100 focus-within:opacity-100 shrink-0"
        />
      )}
    </li>
    {menu.node}
    </>
  );
}

function TerminatorRow({ type, reason, all }: { type: "fail" | "finish"; reason?: string; all?: boolean }): React.JSX.Element {
  const isFail = type === "fail";
  return (
    <li
      {...(all ? { "data-terminator-all": "true" } : {})}
      title={all ? `${type} all — terminates every live branch of the process` : undefined}
      className={`text-xs px-2 py-1 rounded-sm mt-2 inline-flex items-center gap-2 ${isFail ? "bg-red-500/15 text-red-200" : "bg-emerald-500/15 text-emerald-200"}`}
    >
      <span>{isFail ? "✕" : "✓"}</span>
      {reason && <span>{reason}</span>}
      {all && (
        <span className={`uppercase tracking-wide text-[9px] leading-none px-1 py-0.5 rounded border ${isFail ? "border-red-300/60" : "border-emerald-300/60"}`}>
          all
        </span>
      )}
    </li>
  );
}
